import React, { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { useApolloClient } from "@apollo/client";
import { Button, Grid, makeStyles, Typography, ListItemIcon, Menu, MenuItem } from "@material-ui/core";
import { HowToRegOutlined, PersonOutline } from "@material-ui/icons";
import useUserData from "hooks/useUserData";
import { getCurrentDealer, setCurrentDealer } from "hooks/useCurrentDealer";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { emptyArray } from "constants/defaultValues";
import { DEALERS_USER, SET_CURRENT_DEALER } from "features/common/queries";
import menuStyle from "assets/jss/components/menuStyle";

const useStyles = makeStyles(menuStyle);

function DealerSelector() {
  const { t } = useTranslation();
  const classes = useStyles();
  const anchorRef = useRef(null);
  const history = useHistory();
  const cache = useApolloClient();
  const currentDealer = getCurrentDealer(cache);

  const [open, setOpen] = useState(false);

  const [updateCurrentDealer] = useMutationWithErrorHandling(SET_CURRENT_DEALER);

  const { userRights } = useUserData();

  const { data, loading } = useQueryWithErrorHandling(DEALERS_USER, {
    skip: !userRights?.hasVendorAdminUserRight,
    onCompleted: (data) =>
      setCurrentDealer(cache, data.getUserDealers.selectedDealerId, data.getUserDealers.selectedDealerName),
  });
  const dealers = data?.getUserDealers?.dealers || emptyArray;

  const handleClick = useCallback(() => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  const setDealer = useCallback(
    async (id, name) => {
      await updateCurrentDealer({ variables: { dealerId: id } });
      setCurrentDealer(cache, id, name);
    },
    [cache, updateCurrentDealer]
  );

  const handleClickItem = useCallback(
    (dealer) => async () => {
      if (dealer.id != currentDealer.id) {
        await setDealer(dealer.id, dealer.name);
        history.push({ pathname: "/overview" });
      }
      handleClick();
    },
    [history, currentDealer, setDealer, handleClick]
  );

  if (loading || !userRights?.hasVendorAdminUserRight) return <></>;

  return (
    <>
      <Button className={classes.dealerButtonStyle} variant="contained" onClick={handleClick} ref={anchorRef}>
        <HowToRegOutlined />
        <Grid container className={classes.iconDealer} spacing={1} direction="column">
          <Grid item>{t("NavBar.CurrentDealer")}</Grid>
          <Grid item className={classes.dealerStyle}>
            {currentDealer?.name}
          </Grid>
        </Grid>
      </Button>
      {open && (
        <Menu
          id="simple-menu"
          anchorEl={anchorRef.current}
          open={open}
          onClose={handleClick}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
        >
          {dealers.map((dealer) => (
            <MenuItem key={dealer.id} onClick={handleClickItem(dealer)}>
              <ListItemIcon>
                <PersonOutline />
              </ListItemIcon>
              <Typography>{dealer.name}</Typography>
            </MenuItem>
          ))}
        </Menu>
      )}
    </>
  );
}

export default DealerSelector;
